/* Scroll-driven product reveal (three.js).
   Scrubs a frame sequence by scroll position: forward on the way down,
   reverse on the way up. three.js is the sibling three.module.js asset (same
   /assets/ dir → same-origin relative import, which Shopify's CDN and `theme dev`
   both resolve correctly). */
import * as THREE from './three.module.js';

const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

function initSection(root) {
  if (root.dataset.srvInit) return;
  root.dataset.srvInit = '1';

  const stage = root.querySelector('.srv__stage');
  const canvas = root.querySelector('.srv__canvas');
  const loaderEl = root.querySelector('[data-srv-loader]');
  const pctEl = root.querySelector('[data-srv-pct]');
  const topEl = root.querySelector('[data-srv-top]');
  const bottomEl = root.querySelector('[data-srv-bottom]');

  let urls = [];
  try { urls = JSON.parse(root.querySelector('[data-srv-frames]').textContent); } catch (e) {}
  const COUNT = urls.length;
  if (!COUNT || !canvas || !stage) return;

  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  const scene = new THREE.Scene();
  const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 10);
  camera.position.z = 1;

  const texture = new THREE.Texture();
  // Faithful passthrough: the JPEG is already sRGB-encoded and the custom shader
  // writes texels straight out, so DON'T let the GPU sRGB->linear decode them
  // (that darkens/over-saturates — turns the video's yellows orange). NoColorSpace
  // = sample raw stored values = colors match the source video exactly.
  texture.colorSpace = THREE.NoColorSpace;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;
  texture.wrapS = texture.wrapT = THREE.ClampToEdgeWrapping;

  const material = new THREE.ShaderMaterial({
    transparent: true,
    uniforms: { uMap: { value: texture } },
    vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }',
    fragmentShader: 'uniform sampler2D uMap; varying vec2 vUv; void main(){ vec4 c = texture2D(uMap, vUv); vec2 d = min(vUv, 1.0 - vUv); float e = smoothstep(0.0, 0.05, min(d.x, d.y)); gl_FragColor = vec4(c.rgb, c.a * e); }'
  });
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), material);
  scene.add(mesh);

  function layout() {
    const w = stage.clientWidth;
    const h = stage.clientHeight;
    const mobile = w < 760;
    const size = mobile ? Math.min(w * 0.94, h * 0.56) : Math.min(w * 0.6, h * 0.8);
    mesh.scale.set(size, size, 1);
    // nudge the product up a touch so the CTA at the bottom has clear space below it
    mesh.position.y = mobile ? h * 0.01 : h * 0.03;
  }
  function resize() {
    const w = stage.clientWidth;
    const h = stage.clientHeight;
    renderer.setSize(w, h, false);
    camera.left = -w / 2;
    camera.right = w / 2;
    camera.top = h / 2;
    camera.bottom = -h / 2;
    camera.updateProjectionMatrix();
    layout();
  }

  const imgs = new Array(COUNT);
  let loaded = 0;
  for (let i = 0; i < COUNT; i++) {
    const im = new Image();
    im.decoding = 'async';
    im.onload = () => {
      loaded += 1;
      if (pctEl) pctEl.textContent = Math.round((loaded / COUNT) * 100) + '%';
      if (i === 0) {
        texture.image = im;
        texture.needsUpdate = true;
        if (loaderEl) loaderEl.classList.add('is-hidden');
      }
    };
    im.src = urls[i];
    imgs[i] = im;
  }

  let cur = 0;
  let tgt = 0;
  let last = -1;
  let raf;
  let pinState = '';

  function progress() {
    const sc = root.offsetHeight - window.innerHeight;
    const scd = -root.getBoundingClientRect().top;
    return clamp(scd / Math.max(1, sc), 0, 1);
  }
  // Pin the stage by toggling fixed/absolute (sticky is unavailable here).
  function updatePin() {
    const r = root.getBoundingClientRect();
    const vh = window.innerHeight;
    let state = 'start';
    if (r.top <= 0 && r.bottom >= vh) state = 'fixed';
    else if (r.top <= 0) state = 'end';
    if (state === pinState) return;
    pinState = state;
    stage.classList.toggle('is-fixed', state === 'fixed');
    stage.classList.toggle('is-end', state === 'end');
  }

  function applyFrame() {
    const idx = clamp(Math.round(cur), 0, COUNT - 1);
    const im = imgs[idx];
    if (idx !== last && im && im.complete && im.naturalWidth) {
      texture.image = im;
      texture.needsUpdate = true;
      last = idx;
    }
  }

  function render() {
    const r = root.getBoundingClientRect();
    if (r.bottom <= 0 || r.top >= window.innerHeight) return;
    renderer.render(scene, camera);
    const p = cur / (COUNT - 1);
    if (topEl) {
      topEl.style.transform = 'translate3d(0,' + (-p * 90).toFixed(1) + 'px,0)';
      topEl.style.opacity = clamp(1 - p * 1.7, 0, 1).toFixed(3);
    }
    if (bottomEl) {
      const b = clamp((p - 0.55) * 3, 0, 1);
      bottomEl.style.opacity = b.toFixed(3);
      bottomEl.style.transform = 'translate3d(0,' + ((1 - b) * 24).toFixed(1) + 'px,0)';
    }
  }

  // Scroll handler: pin immediately (not rAF-dependent) + set the target frame.
  // If the tab is backgrounded (rAF throttled), snap + render directly.
  function onScroll() {
    updatePin();
    tgt = progress() * (COUNT - 1);
    if (document.hidden) { cur = tgt; applyFrame(); render(); }
  }

  function loop() {
    updatePin();
    cur += (tgt - cur) * 0.16;
    if (Math.abs(tgt - cur) < 0.0008) cur = tgt;
    applyFrame();
    render();
    raf = requestAnimationFrame(loop);
  }

  resize();
  onScroll();
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', resize);
  raf = requestAnimationFrame(loop);

  // Clean up if the theme editor reloads/removes this section.
  function onUnload(e) {
    if (!e.target.contains(root)) return;
    cancelAnimationFrame(raf);
    window.removeEventListener('scroll', onScroll);
    window.removeEventListener('resize', resize);
    document.removeEventListener('shopify:section:unload', onUnload);
    renderer.dispose();
    material.dispose();
    texture.dispose();
  }
  document.addEventListener('shopify:section:unload', onUnload);
}

function boot() {
  document.querySelectorAll('.srv[data-srv]').forEach(initSection);
}

if (document.readyState !== 'loading') boot();
else document.addEventListener('DOMContentLoaded', boot);
document.addEventListener('shopify:section:load', boot);
